/**
 * Los cuadros de la página «Agendas disponibles» (administración, 14/10/2026):
 *  - DiaDialog: lo que se hace con un día en el modo edición —disponibilidad,
 *    vacaciones, día libre, bloquear el día o unas horas de una agenda, quitar
 *    un bloqueo, volver al patrón—;
 *  - AusenciaDialog: marcar vacaciones o días libres de un tramo de fechas;
 *  - PatronBloqueoDialog: alta o cambio de un patrón de bloqueo;
 *  - ConfirmarCitasDialog: «hay N citas esos días; no se anulan».
 * Solo pintan y avisan: qué se guarda y cómo lo decide la página
 * (pages/AgendasDisponiblesPage.jsx) y, de verdad, el servidor.
 */
import React, { useEffect, useState } from 'react';
import { CalendarOff, Loader2, Lock, RotateCcw, Trash2 } from 'lucide-react';
import {
  BICOLOR, DIAS_SEMANA, ROJO, TEXTO_AUSENCIA, VERDE_CONSULTA, VERDE_QUIROFANO,
  esManual, finMaximoDelPatron, horasMal, textoDias, textoDisponibilidad, textoFecha, textoHoras,
  textoTramo, tramoUnico,
} from '../../lib/disponibilidad';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';

const Seccion = ({ titulo, children, testId }) => (
  <div className="space-y-2" data-testid={testId}>
    <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{titulo}</div>
    {children}
  </div>
);

/** Las agendas del profesional, para elegir una: [{id, name, tipo}]. */
export function agendasDe(profesional) {
  return [
    ...(profesional?.consulta || []).map((a) => ({ ...a, tipo: 'consulta' })),
    ...(profesional?.quirofano || []).map((a) => ({ ...a, tipo: 'quirofano' })),
  ];
}

// ---------------------------------------------------------------------------
// Un día, en el modo edición
// ---------------------------------------------------------------------------

/**
 * Props:
 *  - fecha, titulo: el día y cómo se lee («Lunes 2 de marzo de 2099»);
 *  - disp: el día de /calendario (o undefined); bloq: el de /bloqueos;
 *  - profesional: {nombre, consulta: [], quirofano: []};
 *  - patrones: los patrones de bloqueo (para nombrar los de «volver al patrón»);
 *  - ocupado: hay algo guardándose;
 *  - onDisponibilidad(consulta, quirofano, horas), onAusencia(tipo, hasta),
 *    onQuitarAusencia(), onBloquear({agendaId, diaEntero, start, end, motivo}),
 *    onQuitarBloqueo(bloqueo, 'dia' | 'todo'), onVolverAlPatron(patronId,
 *    agendaId), onCerrar().
 *
 * Quitar un bloqueo (revisión del 14/10/2026): uno de varios días ofrece
 * «Quitar solo este día» y «Quitar todo el tramo (del X al Y)» —antes se
 * quitaba el tramo entero sin avisar—; el de un patrón dice en qué agenda se
 * quita el día (solo en esa: las demás del patrón siguen bloqueadas). La
 * confirmación, con el tramo, la pide la página.
 *
 * Las horas (27/10/2026): bajo «Disponibilidad», de cuánto a cuánto viene a
 * cada tipo, dos campos por tipo que salen con las horas de ese día (si las
 * tiene y son un solo tramo) y vacíos si no. Vacíos = todo el día. Al pulsar
 * «Consulta», «Quirófano» o las dos, se mandan las horas de esos tipos
 * (`horas`: {consulta_hora_inicio, …}, solo las rellenas). Las dos o
 * ninguna, y la de fin después de la de inicio; si no, el botón no deja. Son
 * informativas: no bloquean citas ni cambian los huecos.
 */
const SIN_HORAS_DIA = { consulta: { inicio: '', fin: '' }, quirofano: { inicio: '', fin: '' } };

function HorasDeTipo({ tipo, texto, valor, onCambiar, disabled }) {
  const mal = horasMal(valor.inicio, valor.fin);
  return (
    <div data-testid={`disp-dia-horas-${tipo}`}>
      <div className="grid grid-cols-[5.5rem_1fr_1fr] items-center gap-2">
        <span className="text-sm text-[#13231E]">{texto}</span>
        <Input type="time" value={valor.inicio} className="tabular-nums" aria-label={`${texto}: hora de inicio`}
          disabled={disabled} onChange={(e) => onCambiar({ ...valor, inicio: e.target.value })}
          data-testid={`disp-dia-horas-${tipo}-inicio`} />
        <Input type="time" value={valor.fin} className="tabular-nums" aria-label={`${texto}: hora de fin`}
          disabled={disabled} onChange={(e) => onCambiar({ ...valor, fin: e.target.value })}
          data-testid={`disp-dia-horas-${tipo}-fin`} />
      </div>
      {mal && (
        <p className="mt-1 text-[11px] text-[#b91c1c]" data-testid={`disp-dia-horas-${tipo}-mal`}>
          Pon las dos horas (o ninguna), con la de fin después de la de inicio.
        </p>
      )}
    </div>
  );
}

export function DiaDialog({
  fecha, titulo, disp, bloq, profesional, patrones = [], ocupado,
  onDisponibilidad, onAusencia, onQuitarAusencia, onBloquear, onQuitarBloqueo, onVolverAlPatron,
  onCerrar,
}) {
  const agendas = agendasDe(profesional);
  const tieneConsulta = (profesional?.consulta || []).length > 0;
  const tieneQuirofano = (profesional?.quirofano || []).length > 0;
  const [hasta, setHasta] = useState('');
  const [agendaId, setAgendaId] = useState('');
  const [diaEntero, setDiaEntero] = useState(true);
  const [start, setStart] = useState('15:00');
  const [end, setEnd] = useState('20:00');
  const [motivo, setMotivo] = useState('');
  // De cuánto a cuánto viene a cada tipo: {consulta: {inicio, fin}, quirofano: {…}}.
  const [horasDia, setHorasDia] = useState(SIN_HORAS_DIA);

  useEffect(() => {
    if (!fecha) return;
    setHasta(fecha);
    setAgendaId(agendas[0]?.id || '');
    setDiaEntero(true);
    setStart('15:00');
    setEnd('20:00');
    setMotivo('');
    // Las horas que ya tiene ese día (un solo tramo); si no, vacías.
    const deEseDia = (t) => tramoUnico(disp?.[`${t}_horas`]) || { inicio: '', fin: '' };
    setHorasDia({ consulta: deEseDia('consulta'), quirofano: deEseDia('quirofano') });
    // Al abrir otro día, el formulario de cero; no al recargar sus datos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fecha]);

  const malas = (t) => horasMal(horasDia[t].inicio, horasDia[t].fin);
  /** Las horas rellenas de esos tipos, como las pide el servidor; null si ninguna. */
  const cuerpoHoras = (tipos) => {
    const h = {};
    tipos.forEach((t) => {
      if (horasDia[t].inicio && horasDia[t].fin) {
        h[`${t}_hora_inicio`] = horasDia[t].inicio;
        h[`${t}_hora_fin`] = horasDia[t].fin;
      }
    });
    return Object.keys(h).length ? h : null;
  };

  const ausencia = bloq?.ausencia || null;
  // Los de vacaciones y día libre van juntos arriba («Quitar…»): aquí los demás.
  const bloqueos = (bloq?.bloqueos || []).filter((b) => !b.ausencia);
  // [{patron_id, agenda_id, agenda}] (un id suelto, como llegaba antes, vale
  // para todas sus agendas).
  const patronQuitado = (bloq?.patron_quitado || []).map((q) => {
    const quitado = typeof q === 'string' ? { patron_id: q } : (q || {});
    const p = patrones.find((x) => x.id === quitado.patron_id) || { id: quitado.patron_id };
    return { ...quitado, patron: p };
  });
  const horasBloqueoMal = !diaEntero && (!start || !end || end <= start);
  const hastaMal = !hasta || hasta < fecha;

  return (
    <Dialog open={!!fecha} onOpenChange={(v) => { if (!v) onCerrar(); }}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="disp-dia-dialogo">
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
          <DialogDescription data-testid="disp-dia-ahora">
            Ahora: {textoDisponibilidad(disp) || 'sin disponibilidad'}
            {esManual(disp) ? ' (cambiado a mano)' : ''}.
            {esManual(disp) && (
              <> Según el patrón: {textoDisponibilidad(disp, true) || 'no viene'}.</>
            )}
            {ausencia && <> {TEXTO_AUSENCIA[ausencia]}.</>}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Qué días viene, y de cuánto a cuánto */}
          <Seccion titulo="Disponibilidad" testId="disp-dia-disponibilidad">
            <p className="text-xs text-muted-foreground">
              Horas (opcional): de cuánto a cuánto viene. Vacías, todo el día. Solo informan: no
              bloquean citas.
            </p>
            {tieneConsulta && (
              <HorasDeTipo tipo="consulta" texto="Consulta" valor={horasDia.consulta} disabled={ocupado}
                onCambiar={(v) => setHorasDia((h) => ({ ...h, consulta: v }))} />
            )}
            {tieneQuirofano && (
              <HorasDeTipo tipo="quirofano" texto="Quirófano" valor={horasDia.quirofano} disabled={ocupado}
                onCambiar={(v) => setHorasDia((h) => ({ ...h, quirofano: v }))} />
            )}
            <div className="grid gap-2">
              {tieneConsulta && (
                <Button disabled={ocupado || malas('consulta')}
                  onClick={() => onDisponibilidad(true, false, cuerpoHoras(['consulta']))}
                  className="justify-start text-[#052e16] hover:brightness-95"
                  style={{ backgroundColor: VERDE_CONSULTA }} data-testid="disp-opcion-consulta">
                  Consulta
                </Button>
              )}
              {tieneQuirofano && (
                <Button disabled={ocupado || malas('quirofano')}
                  onClick={() => onDisponibilidad(false, true, cuerpoHoras(['quirofano']))}
                  className="justify-start text-white hover:brightness-110"
                  style={{ backgroundColor: VERDE_QUIROFANO }} data-testid="disp-opcion-quirofano">
                  Quirófano
                </Button>
              )}
              {tieneConsulta && tieneQuirofano && (
                <Button disabled={ocupado || malas('consulta') || malas('quirofano')}
                  onClick={() => onDisponibilidad(true, true, cuerpoHoras(['consulta', 'quirofano']))}
                  className="justify-start text-white [text-shadow:0_0_3px_rgba(0,0,0,0.85)]"
                  style={{ backgroundImage: BICOLOR }} data-testid="disp-opcion-ambas">
                  Consulta y quirófano
                </Button>
              )}
              <Button variant="outline" disabled={ocupado} onClick={() => onDisponibilidad(false, false)}
                className="justify-start" data-testid="disp-opcion-ninguna">
                Ninguna (no viene ese día)
              </Button>
              <Button variant="ghost" disabled={ocupado || !esManual(disp)} onClick={() => onDisponibilidad(null, null)}
                className="justify-start" data-testid="disp-opcion-patron">
                <RotateCcw className="w-4 h-4 mr-1.5" /> Volver al patrón
              </Button>
            </div>
          </Seccion>

          {/* Vacaciones y días libres del doctor */}
          <Seccion titulo="Vacaciones y días libres" testId="disp-dia-ausencia">
            <p className="text-xs text-muted-foreground">
              Bloquea todas sus agendas esos días. Si ya hay citas, se avisa antes y no se anulan.
            </p>
            <div>
              <Label htmlFor="disp-ausencia-hasta">Hasta (incluido)</Label>
              <Input id="disp-ausencia-hasta" type="date" value={hasta} min={fecha} className="mt-1.5"
                onChange={(e) => setHasta(e.target.value)} data-testid="disp-ausencia-hasta" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button disabled={ocupado || hastaMal} onClick={() => onAusencia('vacaciones', hasta)}
                className="text-white hover:brightness-110" style={{ backgroundColor: ROJO }}
                data-testid="disp-opcion-vacaciones">
                Vacaciones
              </Button>
              <Button disabled={ocupado || hastaMal} onClick={() => onAusencia('dia_libre', hasta)}
                className="text-white hover:brightness-110" style={{ backgroundColor: ROJO }}
                data-testid="disp-opcion-dia-libre">
                Día libre
              </Button>
            </div>
            {ausencia && (
              <Button variant="outline" disabled={ocupado} onClick={onQuitarAusencia}
                className="w-full justify-start" data-testid="disp-opcion-quitar-ausencia">
                <Trash2 className="w-4 h-4 mr-1.5" /> Quitar {TEXTO_AUSENCIA[ausencia].toLowerCase()} de este día
              </Button>
            )}
          </Seccion>

          {/* Bloquear una agenda ese día */}
          {agendas.length > 0 && (
            <Seccion titulo="Bloquear una agenda este día" testId="disp-dia-bloquear">
              <div>
                <Label htmlFor="disp-bloqueo-agenda">Agenda</Label>
                <select id="disp-bloqueo-agenda" value={agendaId} onChange={(e) => setAgendaId(e.target.value)}
                  className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
                  data-testid="disp-bloqueo-agenda">
                  {agendas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-[#13231E]">
                <input type="checkbox" checked={diaEntero} onChange={(e) => setDiaEntero(e.target.checked)}
                  className="h-4 w-4 accent-[#b91c1c]" data-testid="disp-bloqueo-dia-entero" />
                El día entero (si no, unas horas)
              </label>
              {!diaEntero && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="disp-bloqueo-start">Desde las</Label>
                    <Input id="disp-bloqueo-start" type="time" value={start} className="mt-1.5 tabular-nums"
                      onChange={(e) => setStart(e.target.value)} data-testid="disp-bloqueo-start" />
                  </div>
                  <div>
                    <Label htmlFor="disp-bloqueo-end">Hasta las</Label>
                    <Input id="disp-bloqueo-end" type="time" value={end} className="mt-1.5 tabular-nums"
                      onChange={(e) => setEnd(e.target.value)} data-testid="disp-bloqueo-end" />
                  </div>
                </div>
              )}
              <div>
                <Label htmlFor="disp-bloqueo-motivo">Motivo</Label>
                <Input id="disp-bloqueo-motivo" value={motivo} maxLength={200} className="mt-1.5"
                  placeholder="Por ejemplo: congreso, reunión, obras"
                  onChange={(e) => setMotivo(e.target.value)} data-testid="disp-bloqueo-motivo" />
              </div>
              <Button disabled={ocupado || !agendaId || !motivo.trim() || horasBloqueoMal}
                onClick={() => onBloquear({ agendaId, diaEntero, start, end, motivo: motivo.trim() })}
                className="w-full bg-[#b91c1c] hover:bg-[#991b1b] text-white" data-testid="disp-bloqueo-guardar">
                <Lock className="w-4 h-4 mr-1.5" />
                {diaEntero ? 'Bloquear el día' : `Bloquear de ${start} a ${end}`}
              </Button>
            </Seccion>
          )}

          {/* Lo que ya lo bloquea */}
          {(bloqueos.length > 0 || patronQuitado.length > 0) && (
            <Seccion titulo="Bloqueos de este día" testId="disp-dia-bloqueos">
              <ul className="divide-y divide-border rounded-lg border border-border">
                {bloqueos.map((b) => {
                  const tramo = b.date_from && b.date_to && b.date_from !== b.date_to;
                  return (
                    <li key={`${b.id}-${b.agenda_id}`} className="px-3 py-2 text-sm space-y-2"
                      data-testid={`disp-dia-bloqueo-${b.id}`}>
                      <div className="flex items-start gap-2">
                        <CalendarOff className="w-3.5 h-3.5 mt-0.5 text-[#b91c1c] shrink-0" />
                        <div className="min-w-0 flex-1">
                          <span className="font-medium">{b.agenda || 'Agenda'}</span>
                          <span className="text-muted-foreground"> · {textoHoras(b)}</span>
                          {tramo && (
                            <span className="text-muted-foreground"> · {textoTramo(b.date_from, b.date_to)}</span>
                          )}
                          {b.reason ? <span className="text-muted-foreground"> · {b.reason}</span> : null}
                          {b.patron_id ? <span className="text-muted-foreground"> · patrón</span> : null}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5 pl-5">
                        {b.patron_id ? (
                          <Button variant="outline" size="sm" disabled={ocupado}
                            className="h-auto min-h-8 whitespace-normal text-left"
                            onClick={() => onQuitarBloqueo(b, 'dia')}
                            data-testid={`disp-quitar-bloqueo-${b.id}`}>
                            Quitar este día del patrón en {b.agenda || 'esta agenda'}
                          </Button>
                        ) : tramo ? (
                          <>
                            <Button variant="outline" size="sm" disabled={ocupado}
                              onClick={() => onQuitarBloqueo(b, 'dia')}
                              data-testid={`disp-quitar-dia-bloqueo-${b.id}`}>
                              Quitar solo este día
                            </Button>
                            <Button variant="outline" size="sm" disabled={ocupado}
                              className="h-auto min-h-8 whitespace-normal text-left"
                              onClick={() => onQuitarBloqueo(b, 'todo')}
                              data-testid={`disp-quitar-bloqueo-${b.id}`}>
                              Quitar todo el tramo (del {textoFecha(b.date_from)} al {textoFecha(b.date_to)})
                            </Button>
                          </>
                        ) : (
                          <Button variant="outline" size="sm" disabled={ocupado}
                            onClick={() => onQuitarBloqueo(b, 'todo')}
                            data-testid={`disp-quitar-bloqueo-${b.id}`}>
                            Quitar el bloqueo
                          </Button>
                        )}
                      </div>
                    </li>
                  );
                })}
                {patronQuitado.map((q) => {
                  const p = q.patron;
                  const sufijo = q.agenda_id ? `-${q.agenda_id}` : '';
                  return (
                    <li key={`patron-${q.patron_id}${sufijo}`} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm"
                      data-testid={`disp-dia-patron-quitado-${q.patron_id}${sufijo}`}>
                      <RotateCcw className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      <div className="min-w-0 flex-1 text-muted-foreground">
                        Quitado del patrón{p.motivo ? ` «${p.motivo}»` : ''}
                        {p.dias_semana ? ` (${textoDias(p.dias_semana)}, ${textoHoras(p)})` : ''}
                        {q.agenda ? ` en ${q.agenda}` : ''}
                      </div>
                      <Button variant="outline" size="sm" disabled={ocupado}
                        onClick={() => onVolverAlPatron(q.patron_id, q.agenda_id || null)}
                        data-testid={`disp-volver-patron-bloqueo-${q.patron_id}${sufijo}`}>
                        Volver al patrón
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </Seccion>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Vacaciones o días libres de un tramo
// ---------------------------------------------------------------------------

export function AusenciaDialog({ datos, setDatos, nombre, ocupado, onGuardar, onCerrar }) {
  const mal = !datos?.desde || (datos?.hasta && datos.hasta < datos.desde);
  return (
    <Dialog open={!!datos} onOpenChange={(v) => { if (!v) onCerrar(); }}>
      <DialogContent className="max-w-sm" data-testid="disp-ausencia-dialogo">
        <DialogHeader>
          <DialogTitle>Vacaciones o día libre</DialogTitle>
          <DialogDescription>
            {nombre}: se bloquean todas sus agendas esos días. Si ya hay citas, se avisa antes y no se anulan.
          </DialogDescription>
        </DialogHeader>
        {datos && (
          <div className="space-y-4">
            <div className="flex gap-2" role="radiogroup" aria-label="Qué es">
              {[['vacaciones', 'Vacaciones'], ['dia_libre', 'Día libre']].map(([valor, texto]) => (
                <button key={valor} type="button" role="radio" aria-checked={datos.tipo === valor}
                  onClick={() => setDatos((d) => ({ ...d, tipo: valor }))}
                  data-testid={`disp-ausencia-tipo-${valor}`}
                  className={`px-3 py-1.5 rounded-md border text-sm font-medium ${
                    datos.tipo === valor ? 'text-white border-[#b91c1c] bg-[#b91c1c]' : 'border-[#D9E3DD] text-[#13231E]'}`}>
                  {texto}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="disp-ausencia-desde">Desde</Label>
                <Input id="disp-ausencia-desde" type="date" value={datos.desde} className="mt-1.5"
                  onChange={(e) => setDatos((d) => ({ ...d, desde: e.target.value }))}
                  data-testid="disp-ausencia-form-desde" />
              </div>
              <div>
                <Label htmlFor="disp-ausencia-form-hasta">Hasta</Label>
                <Input id="disp-ausencia-form-hasta" type="date" value={datos.hasta} min={datos.desde}
                  className="mt-1.5" onChange={(e) => setDatos((d) => ({ ...d, hasta: e.target.value }))}
                  data-testid="disp-ausencia-form-hasta" />
              </div>
            </div>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCerrar} disabled={ocupado}>Cancelar</Button>
          <Button onClick={onGuardar} disabled={ocupado || mal}
            className="bg-[#b91c1c] hover:bg-[#991b1b] text-white" data-testid="disp-ausencia-guardar">
            {ocupado && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Marcar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// «Hay citas esos días»
// ---------------------------------------------------------------------------

/** `confirmacion`: {detail, citas, textoSi?} (el 409 del servidor). Sirve
 *  para las vacaciones, los patrones de bloqueo, bloquear un día y completar
 *  un bloqueo: el botón dice lo que se hace igualmente (`textoSi`). */
export function ConfirmarCitasDialog({ confirmacion, ocupado, onConfirmar, onCancelar }) {
  return (
    <Dialog open={!!confirmacion} onOpenChange={(v) => { if (!v) onCancelar(); }}>
      <DialogContent className="max-w-md" data-testid="disp-confirmar-citas">
        <DialogHeader>
          <DialogTitle>
            {confirmacion?.citas === 1 ? 'Hay 1 cita esos días' : `Hay ${confirmacion?.citas} citas esos días`}
          </DialogTitle>
          <DialogDescription data-testid="disp-confirmar-citas-texto">
            {confirmacion?.detail}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancelar} disabled={ocupado} data-testid="disp-confirmar-citas-no">
            Cancelar
          </Button>
          <Button onClick={onConfirmar} disabled={ocupado}
            className="bg-[#b91c1c] hover:bg-[#991b1b] text-white" data-testid="disp-confirmar-citas-si">
            {ocupado && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {confirmacion?.textoSi || 'Marcar igualmente (sin anular citas)'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Patrón de bloqueo
// ---------------------------------------------------------------------------

export const formPatronBloqueoVacio = (hoy, profesional) => ({
  id: null,
  agendas: agendasDe(profesional).slice(0, 1).map((a) => a.id),
  dias: [],
  desde: hoy,
  hasta: finMaximoDelPatron(hoy),
  diaEntero: false,
  start: '15:00',
  end: '20:00',
  motivo: '',
});

export function PatronBloqueoDialog({ form, setForm, profesional, ocupado, onGuardar, onCerrar }) {
  const agendas = agendasDe(profesional);
  const maximo = form ? finMaximoDelPatron(form.desde) : '';
  const alternar = (campo, valor) => setForm((f) => ({
    ...f,
    [campo]: f[campo].includes(valor) ? f[campo].filter((x) => x !== valor) : [...f[campo], valor],
  }));
  const mal = !form || !form.agendas.length || !form.dias.length || !form.desde || !form.hasta
    || form.hasta < form.desde || (maximo && form.hasta > maximo) || !form.motivo.trim()
    || (!form.diaEntero && (!form.start || !form.end || form.end <= form.start));
  return (
    <Dialog open={!!form} onOpenChange={(v) => { if (!v) onCerrar(); }}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="disp-pb-dialogo">
        <DialogHeader>
          <DialogTitle>{form?.id ? 'Editar patrón de bloqueo' : 'Añadir patrón de bloqueo'}</DialogTitle>
          <DialogDescription>
            {profesional?.nombre}: «todos los lunes por la tarde, bloqueado». Se bloquean esos días de
            «desde» a «hasta» (como mucho 12 meses); un día suelto se puede quitar sin romper el patrón.
          </DialogDescription>
        </DialogHeader>
        {form && (
          <div className="space-y-4">
            <fieldset>
              <legend className="text-sm font-medium leading-none">Agendas</legend>
              <div className="mt-2 grid gap-1.5">
                {agendas.map((a) => (
                  <label key={a.id} className="inline-flex items-center gap-2 text-sm text-[#13231E]">
                    <input type="checkbox" checked={form.agendas.includes(a.id)}
                      onChange={() => alternar('agendas', a.id)} className="h-4 w-4 accent-[#b91c1c]"
                      data-testid={`disp-pb-agenda-${a.id}`} />
                    {a.name}
                  </label>
                ))}
              </div>
            </fieldset>
            <fieldset>
              <legend className="text-sm font-medium leading-none">Días de la semana</legend>
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1.5">
                {DIAS_SEMANA.map((nombre, i) => (
                  <label key={nombre} className="inline-flex items-center gap-2 text-sm text-[#13231E]">
                    <input type="checkbox" checked={form.dias.includes(i)}
                      onChange={() => alternar('dias', i)} className="h-4 w-4 accent-[#b91c1c]"
                      data-testid={`disp-pb-dia-${i}`} />
                    {nombre}
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="disp-pb-desde">Desde</Label>
                <Input id="disp-pb-desde" type="date" value={form.desde} className="mt-1.5"
                  onChange={(e) => {
                    const desde = e.target.value;
                    // Un «hasta» que ya no vale con este «desde» pasa al máximo (12 meses).
                    setForm((f) => {
                      const tope = finMaximoDelPatron(desde);
                      const vale = f.hasta && f.hasta >= desde && (!tope || f.hasta <= tope);
                      return { ...f, desde, hasta: vale ? f.hasta : (tope || f.hasta) };
                    });
                  }} data-testid="disp-pb-desde" />
              </div>
              <div>
                <Label htmlFor="disp-pb-hasta">Hasta</Label>
                <Input id="disp-pb-hasta" type="date" value={form.hasta} min={form.desde} max={maximo || undefined}
                  className="mt-1.5" onChange={(e) => setForm((f) => ({ ...f, hasta: e.target.value }))}
                  data-testid="disp-pb-hasta" />
                <p className="text-[11px] text-muted-foreground mt-1">Como mucho 12 meses.</p>
              </div>
            </div>
            <div>
              <label className="inline-flex items-center gap-2 text-sm text-[#13231E]">
                <input type="checkbox" checked={form.diaEntero}
                  onChange={(e) => setForm((f) => ({ ...f, diaEntero: e.target.checked }))}
                  className="h-4 w-4 accent-[#b91c1c]" data-testid="disp-pb-dia-entero" />
                El día entero (si no, unas horas)
              </label>
              {!form.diaEntero && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="disp-pb-start">Desde las</Label>
                    <Input id="disp-pb-start" type="time" value={form.start} className="mt-1.5 tabular-nums"
                      onChange={(e) => setForm((f) => ({ ...f, start: e.target.value }))} data-testid="disp-pb-start" />
                  </div>
                  <div>
                    <Label htmlFor="disp-pb-end">Hasta las</Label>
                    <Input id="disp-pb-end" type="time" value={form.end} className="mt-1.5 tabular-nums"
                      onChange={(e) => setForm((f) => ({ ...f, end: e.target.value }))} data-testid="disp-pb-end" />
                  </div>
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="disp-pb-motivo">Motivo</Label>
              <Input id="disp-pb-motivo" value={form.motivo} maxLength={200} className="mt-1.5"
                placeholder="Es lo que se lee en la agenda" data-testid="disp-pb-motivo"
                onChange={(e) => setForm((f) => ({ ...f, motivo: e.target.value }))} />
            </div>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCerrar} disabled={ocupado}>Cancelar</Button>
          {/* Los de guardar, en el verde de la casa (botonesGuardar.test.js). */}
          <Button onClick={onGuardar} disabled={ocupado || mal}
            className="bg-[#2E6E5E] hover:bg-[#1E4A3F] text-white" data-testid="disp-pb-guardar">
            {ocupado && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {form?.id ? 'Guardar cambios' : 'Añadir patrón'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
